import { useEditor as useTiptapEditor } from "@tiptap/react";
import { useEffect, useImperativeHandle } from "react";
import type { EditorHookProps } from "../types/hook";
import { CoreEditorExtensions } from "../extentions/extention";
import { getEditorRefHelpers } from "../helpers/editor-ref";
import { CoreEditorProps } from "../props";

export const useEditor = (props: EditorHookProps) => {
  const {
    autofocus = false,
    editable = true,
    editorProps = {},
    extensions = [],
    forwardedRef,
    handleEditorReady,
    initialValue,
    isTouchDevice,
    mentionHandler,
    onChange,
    onEditorFocus,
    onTransaction,
    placeholder,
    editorClassName = "",
    value,
    id
  } = props;

  const editor = useTiptapEditor(
    {
      editable,
      immediatelyRender: false,
      shouldRerenderOnTransaction: false,
      autofocus,
      parseOptions: { preserveWhitespace: true },
      editorProps: {
        ...CoreEditorProps({
          editorClassName
        }),
        ...editorProps
      },
      extensions: [
        ...CoreEditorExtensions({
          mentionHandler,
          isTouchDevice,
          editable,
          placeholder
        }),
        ...extensions
      ],
      content: initialValue,
      onCreate: () => handleEditorReady?.(true),
      onTransaction: () => {
        onTransaction?.();
      },
      onUpdate: ({ editor: instance }) =>
        onChange?.(instance.getJSON(), instance.getHTML()),
      onDestroy: () => handleEditorReady?.(false),
      onFocus: onEditorFocus
    },
    [editable]
  );

  // Effect for syncing data
  useEffect(() => {
    if (value === null || value === undefined) return;
    if (editor)
      if (!editor.isDestroyed)
        try {
          editor.commands.setContent(value, {
            parseOptions: { preserveWhitespace: true },
            emitUpdate: false
          });
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- it's necessary to check if the selection is valid
          if (editor.state.selection) {
            const docLength = editor.state.doc.content.size;
            const relativePosition = Math.min(
              editor.state.selection.from,
              docLength - 1
            );
            editor.commands.setTextSelection(relativePosition);
          }
        } catch (error) {
          console.error(
            "Error syncing editor content with external value:",
            error
          );
        }
  }, [editor, value, id]);

  useImperativeHandle(forwardedRef, () => getEditorRefHelpers({ editor }), [
    editor
  ]);

  return editor;
};
